import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getBudgetLabel, getTierLabel } from '@utils/sponsor';

interface SponsorApplicationRow {
  id: string;
  guestCompanyName?: string;
  guestContactName?: string;
  guestContactEmail?: string;
  guestContactPhone?: string;
  opportunityTitle?: string;
  sports?: string[];
  interestCategories?: string[];
  preferredTier?: string;
  budgetBand?: string;
  country?: string;
  message?: string;
  status: string;
  createdAt?: string;
}

const statusColors: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  submitted: 'warning',
  under_review: 'info',
  approved: 'success',
  declined: 'error',
  withdrawn: 'default',
};

const AdminSponsorApplications: React.FC = () => {
  const [rows, setRows] = useState<SponsorApplicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const res = await fetch(`/api/admin/sponsor-applications${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load');
      setRows(data.data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const updateStatus = async (id: string, status: string) => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      await fetch(`/api/admin/sponsor-applications/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status }),
      });
      void load();
    } catch {
      // ignore
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>Sponsor leads & applications</Typography>
          <Typography variant="body2" color="text.secondary">
            Phase 1 auto-approves new interest; use status changes when manual review is enabled later.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(String(e.target.value))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="under_review">Under review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="declined">Declined</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => void load()}>Refresh</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 800 }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Opportunity</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Tier / Budget</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>No applications yet</TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.guestCompanyName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.country}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.guestContactName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.guestContactEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.opportunityTitle || 'Open interest'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(row.sports || []).join(', ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">{getTierLabel(row.preferredTier)}</Typography>
                      <Typography variant="caption" color="text.secondary">{getBudgetLabel(row.budgetBand)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColors[row.status] || 'default'} sx={{ fontWeight: 800, textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Button size="small" onClick={() => void updateStatus(row.id, 'under_review')}>Review</Button>
                        <Button size="small" color="success" onClick={() => void updateStatus(row.id, 'approved')}>Approve</Button>
                        <Button size="small" color="error" onClick={() => void updateStatus(row.id, 'declined')}>Decline</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {rows.some((r) => r.message) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Recent messages</Typography>
          {rows.filter((r) => r.message).slice(0, 5).map((row) => (
            <Paper key={`msg-${row.id}`} sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{row.guestCompanyName}</Typography>
              <Typography variant="body2" color="text.secondary">{row.message}</Typography>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AdminSponsorApplications;
